import Tabs from '@src/components/common/Tabs';
import useVideoCreatorDataInitialization from '@src/hooks/useVideoCreatorDataInitialization';
import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useCallback } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import AIVoicesSection from './sections/AIVoicesSection';
import DistributionChannelsSection from './sections/DistributionChannelsSection';
import TranslationMemorySection from './sections/TranslationMemorySection';

import type { Tab } from '@src/components/common/Tabs';

const DEFAULT_TAB = 'social';

const Settings = () => {
  const userIdentity = useUserIdentity();
  const { enableSettingsScreen, enableAiVoices, enableTranslationMemory } =
    useFlagsCombination(userIdentity.currentRole);

  // Same data-init pattern as HomePage and BrandKit: kicks off the showcase
  // fetch from URL params if it isn't already loaded. Sections below depend
  // on `videoCreator.organization` being populated.
  useVideoCreatorDataInitialization();

  // The active tab is RESTful — it lives in a `?tab=` query param so a refresh
  // (or a shared link) restores the tab you were on, preserving the other
  // params (e.g. the showcase). Written on change, read at render.
  const [searchParams, setSearchParams] = useSearchParams();
  const handleTabChange = useCallback(
    (tabId: string) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set('tab', tabId);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  if (!enableSettingsScreen) {
    return <Navigate to={`/home${window.location.search}`} replace />;
  }

  // Second-level tabbed menu. "Social" (Distribution Channels) always shows;
  // "AI Voices" (AI dubbing activation) is dark-launched behind `enableAiVoices`
  // and rendered by the AI Voices surface tickets (PLAT-10499/10500/10504).
  // "Translation Memory" (glossary curation) is dark-launched behind
  // `enableTranslationMemory` (PLAT-10844).
  const tabs: Tab[] = [
    { id: 'social', label: 'Social', content: <DistributionChannelsSection /> },
    ...(enableAiVoices
      ? [
          {
            id: 'ai-voices',
            label: 'AI Voices',
            content: <AIVoicesSection />,
          },
        ]
      : []),
    ...(enableTranslationMemory
      ? [
          {
            id: 'translation-memory',
            label: 'Translation Memory',
            content: <TranslationMemorySection />,
          },
        ]
      : []),
  ];

  // Honour the URL tab when it's an available tab, else fall back to Social
  // (e.g. a `?tab=ai-voices` link while the flag is off).
  const tabParam = searchParams.get('tab');
  const activeTab = tabs.some(t => t.id === tabParam) ? tabParam! : DEFAULT_TAB;

  return (
    <Main>
      <div className="container">
        <div className="topbar">
          <h1>Settings</h1>
        </div>
        <div className="main">
          {/* Remount when the available tab set changes (flag resolves) so the
              URL tab is re-read into the initial selection. */}
          <Tabs
            key={tabs.map(t => t.id).join('|')}
            tabs={tabs}
            defaultTab={activeTab}
            onChange={handleTabChange}
          />
        </div>
      </div>
    </Main>
  );
};

export default Settings;

const Main = styled.main`
  min-height: calc(100vh - 69px);
  display: flex;
  flex-direction: column;

  *,
  *:before,
  *:after {
    box-sizing: border-box;
  }

  .container {
    flex-grow: 1;
    width: 100%;
    max-width: 1488px;
    margin: 0 auto;
    padding: 40px 20px;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
    grid-template-areas:
      'topbar'
      'main';
    grid-row-gap: 8px;
  }

  .topbar {
    grid-area: topbar;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--border);
    margin-bottom: 8px;

    > h1 {
      font-size: 32px;
      font-weight: var(--font-weight-bold);
      color: var(--foreground);
      margin: 0;
    }
  }

  .main {
    grid-area: main;
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
  }
`;
